
import { Text } from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AppContext } from '@r-app/app-context';
import { AnnotationPresetsPanel, BlueprintViewContainer, type BlueprintViewContainerProps } from '../blueprint-view';
import { raceFrame } from '../blueprint-view/helpers/race-frame';
import { DisplayMode } from '@r-core/controller';
import { renderTracking } from '@r-services/render-tracking/render-tracking-service';
import { ViewportControl } from '@r-services/viewport';
import { InformationBox, InformationBoxPosition } from '@r-components';

import { ContextMenuInformation } from './context-menu-information';
import { DesignDetails } from './design-details';
import { FpsInformation } from './fps-information';
import { RenderInformation } from './render-information';
import { ShowInteractionInfo } from '../../services/ui-interactions/show-interaction-info';
import { useInteraction } from '@r-services/ui-interactions';
import { useViewport } from '@r-services/viewport/use-viewport';
import { ContextMenu, useContextMenu } from '../../context-menu';
import { SkuAnnotationProvider, SkuAnnotationToolbar, useSkuAnnotation, useSkuAnnotationEvents } from '../sku-annotations';
import type { AnnotationPreset } from '../sku-annotations/types';

// Keeps the presets panel clear of SKU overhead's own top-left state / design-details overlay
const SKU_PRESETS_PANEL_STYLE: React.CSSProperties = { top: 'auto', bottom: '12px' };

type ShowDesignerProps = {
	children: React.ReactElement | Array<React.ReactElement>;
	backgroundColor: string;
	onSaveAnnotationPreset?: (name: string, annotations: Array<Record<string, unknown>>) => void;
	annotationPresets?: Array<AnnotationPreset>;
	onDeleteAnnotationPreset?: (presetId: string) => void;
	/** Fired after a blueprint PDF/PPTX download; react-ui saves the design so the dates the export stamped are persisted. */
	onBlueprintExported?: () => void;
};

export const Designer = (props: ShowDesignerProps): React.ReactElement => {
	const { backgroundColor, children, onSaveAnnotationPreset, annotationPresets, onDeleteAnnotationPreset, onBlueprintExported } = props;

	renderTracking.incrementRender('Designer');

	return (
		<SkuAnnotationProvider onSaveAnnotationPreset={onSaveAnnotationPreset} annotationPresets={annotationPresets} onDeleteAnnotationPreset={onDeleteAnnotationPreset}>
			<DesignerContent backgroundColor={backgroundColor} onBlueprintExported={onBlueprintExported}>
				{children}
			</DesignerContent>
		</SkuAnnotationProvider>
	);
};

const DesignerContent = (props: ShowDesignerProps): React.ReactElement => {
	const { backgroundColor, children, onBlueprintExported } = props;
	const context = useContext(AppContext);
	const controller = context?.controller;

	const svgRef = useRef<SVGSVGElement>(null);
	const { actions: viewportActions } = useViewport();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_interactionData, { setSvg: setInteractionSvg }] = useInteraction();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_contextMenuData, { setSvg: setContextMenuSvg }] = useContextMenu();

	const {
		activeTool,
		setActiveTool,
		editingAnnotationId,
		setEditingAnnotationId,
		selectedAnnotationId,
		setSelectedAnnotationId,
		setContextMenu,
		setActiveTextSelection,
		setColorRunsSourceText,
		setFormatSelection,
		setStylePreview
	} = useSkuAnnotation();

	// Set SVG elements when they become available
	useEffect(() => {
		if (svgRef?.current) {
			setInteractionSvg(svgRef?.current);
			setContextMenuSvg(svgRef?.current);
		}
	}, [svgRef?.current, viewportActions, setInteractionSvg, setContextMenuSvg]);

	useSkuAnnotationEvents({ controller, kitchen: controller?.kitchen, selectedAnnotationId, setSelectedAnnotationId, setEditingAnnotationId, setActiveTool });

	// A preset placed here lands on the SKU-overhead surface (pageNumber 0), centred on what the user is actually looking at
	const getSkuPageNumber = useCallback((): number => 0, []);
	const getSkuViewportCenter = useCallback((): { x: number; y: number } => {
		const [x, y, width, height] = (controller?.viewport?.getViewBox() ?? '').split(',').map(Number);
		if (![x, y, width, height].every((value) => Number.isFinite(value))) return { x: 0, y: 0 };
		return { x: x + (width / 2), y: y + (height / 2) };
	}, [controller]);

	// SKU Overhead and Blueprint View mount mutually exclusively but share this one Provider (#354
	// audit): reset tool/selection/editing/menu state at the boundary so neither surface inherits it.
	const isBlueprintView = controller?.navigation?.displayMode === DisplayMode.BlueprintView;
	useEffect(() => {
		setActiveTool('select');
		setSelectedAnnotationId(null);
		setEditingAnnotationId(null);
		setContextMenu(null);
		setActiveTextSelection(null);
		setColorRunsSourceText(null);
		setFormatSelection(null);
		setStylePreview(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isBlueprintView]);

	if (!controller) {
		return <div>Loading...</div>;
	}

	const { kitchen, options, navigation } = controller;

	if (navigation.displayMode === DisplayMode.BlueprintView) {
		const { customerName, designerName, designerInitials, currentUserInitials, layoutDescription, creationDate, threeDImageUrl } = options;
		return (
			<DeferredBlueprintView
				customerName={customerName}
				designerName={designerName}
				designerInitials={designerInitials}
				currentUserInitials={currentUserInitials}
				layoutDescription={layoutDescription}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				onDownloadPdf={onBlueprintExported}
				onDownloadPptx={onBlueprintExported}
			/>
		);
	}

	const island = kitchen.design.islands.findById(navigation.islandId);
	const panel = island?.panels.findById(navigation.panelId);

	// kitchen.isLocal is the canonical unsaved-changes flag — '(unsaved)' must never appear without it
	const bpState = controller.readonly
		? 'READ-ONLY'
		: [kitchen.state, kitchen.isLocal ? '(unsaved)' : ''].filter((part) => part !== '').join(' ');
	const isSkuMode = navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing;

	return (
		<div
			// ref={containerRef}
			style={{
				width: '100%',
				height: '100%',
				position: 'relative',
				overflow: 'hidden'
			}}
		>

			{/* SKU Annotation Toolbar */}
			<SkuAnnotationToolbar
				activeTool={activeTool}
				onToolChange={(tool) => {
					setActiveTool(tool);
				}}
				selectedAnnotation={(editingAnnotationId ?? selectedAnnotationId) ? kitchen.skuAnnotations?.findById(editingAnnotationId ?? selectedAnnotationId ?? '') : null}
				annotations={kitchen.skuAnnotations ?? null}
			/>

			{/* Saved annotation presets (#354) -- the same account-wide list blueprint view shows */}
			{isSkuMode && (
				<AnnotationPresetsPanel
					getCurrentPageNumber={getSkuPageNumber}
					getViewportCenter={getSkuViewportCenter}
					annotations={kitchen.skuAnnotations ?? null}
					style={SKU_PRESETS_PANEL_STYLE}
				/>
			)}


			{/* Top Left Information Box */}
			<InformationBox location={InformationBoxPosition.TopLeft} condition={options.showState}>
				<Text fontSize="xl" align="left" color="red">
					{bpState}
				</Text>
				{isSkuMode &&
					<DesignDetails options={options} kitchen={kitchen} />
				}
			</InformationBox>

			{/* Top Right Information Box - Blueprint */}
			<InformationBox location={InformationBoxPosition.TopRight} condition={options.showTitle}>
				<Text fontSize="m" align="right" color="gray:600">
					{navigation.displayMode === DisplayMode.Overhead ? 'Overhead' : panel?.label ?? 'Countertop'}
				</Text>
			</InformationBox>

			{/* Bottom Left Information Box - Developer Insights */}
			<InformationBox
				location={InformationBoxPosition.BottomLeft}
				condition={options.showDeveloperInsights}
			>
				<ContextMenuInformation />
				<ShowInteractionInfo svg={svgRef} />
			</InformationBox>

			{/* Bottom Right Information Box */}
			<InformationBox location={InformationBoxPosition.BottomRight} condition={options.showDeveloperInsights}>
				<RenderInformation/>
				<FpsInformation options={options}></FpsInformation>
			</InformationBox>

			<ContextMenu svg={svgRef}>
				<ViewportControl
					ref={svgRef}
					backgroundColor={backgroundColor}
					viewportService={controller.viewport}
				>
					{children}
				</ViewportControl>
			</ContextMenu>
		</div>
	);
};

/**
 * Shows a loading spinner immediately when entering blueprint view, then
 * defers the heavy `BlueprintViewContainer` mount to the next animation
 * frame so the browser has time to paint the spinner first.
 */
const DeferredBlueprintView = (props: BlueprintViewContainerProps): React.ReactElement => {
	const [ready, setReady] = useState(false);

	useEffect(() => raceFrame(() => setReady(true)), []);

	if (!ready) {
		return (
			<div style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: '#e5e5e5'
			}}>
				<style>{'@keyframes bp-spin { to { transform: rotate(360deg); } }'}</style>
				<div style={{ textAlign: 'center' }}>
					<div style={{
						width: '48px',
						height: '48px',
						border: '4px solid #ddd',
						borderTopColor: '#666',
						borderRadius: '50%',
						animation: 'bp-spin 0.8s linear infinite',
						margin: '0 auto 12px auto'
					}} />
					<div style={{ fontSize: '13px', color: '#666', fontFamily: "'Inter', sans-serif" }}>
						Loading Blueprint View...
					</div>
				</div>
			</div>
		);
	}

	return <BlueprintViewContainer {...props} />;
};
